import { useCallback, useRef, useState } from "react";
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	Alert,
	AlertIcon,
	Text,
	Spinner,
} from "@chakra-ui/react";
import { FiUpload, FiPlus, FiTrash2 } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { cn } from "utils/helpers";
import type {
	AiImportTable,
	AiSpreadsheetImportPreview,
} from "../../api/ai-spreadsheet-import-api";
import { analyseSpreadsheet, saveAiImportTables } from "../../api";

export interface AiSpreadsheetImportProps {
	isOpen: boolean;
	rfxId: number;
	onClose: () => void;
	onImportComplete: () => void;
}

const COLUMN_TYPE_OPTIONS = [
	{ value: "response_value", label: "Response Value" },
	{ value: "saving", label: "Response Saving" },
	{ value: "response_quantity", label: "Response Quantity" },
	{ value: "response_number", label: "Response Number" },
	{ value: "response_text", label: "Response Text" },
	{ value: "description", label: "Description" },
	{ value: "unit_of_measurement", label: "Unit of Measurement" },
	{ value: "total", label: "Total" },
];

const cellBorder = "border border-[#EAECF0]";

const AiSpreadsheetImport: React.FC<AiSpreadsheetImportProps> = ({
	rfxId,
	isOpen,
	onClose,
	onImportComplete,
}) => {
	const [preview, setPreview] = useState<AiSpreadsheetImportPreview | null>(null);
	const [isAnalysing, setIsAnalysing] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState("");
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [activeTableIndex, setActiveTableIndex] = useState(0);

	const handleFileSelect = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const selectedFile = e.target.files?.[0];
			if (!selectedFile) {
				return;
			}

			const allowedTypes = [
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				"application/vnd.ms-excel",
			];
			if (!allowedTypes.includes(selectedFile.type)) {
				setError("Please select an Excel file (.xlsx or .xls)");
				return;
			}

			setError("");
			setIsAnalysing(true);
			setPreview(null);

			try {
				const result = await analyseSpreadsheet(rfxId, selectedFile);
				setPreview(result);
				setActiveTableIndex(0);
			} catch {
				setError(
					"Could not parse this spreadsheet. Please check the file format and try again."
				);
			} finally {
				setIsAnalysing(false);
			}
		},
		[rfxId]
	);

	const handleTableNameChange = useCallback(
		(tableIndex: number, name: string) => {
			if (!preview) {
				return;
			}
			const updated = [...preview.tables];
			updated[tableIndex] = { ...updated[tableIndex], name };
			setPreview({ ...preview, tables: updated });
		},
		[preview]
	);

	const handleColumnTypeChange = useCallback(
		(tableIndex: number, colIndex: number, newType: string) => {
			if (!preview) {
				return;
			}
			const updated = [...preview.tables];
			const cols = [...updated[tableIndex].columns];
			cols[colIndex] = { ...cols[colIndex], detectedType: newType };
			updated[tableIndex] = { ...updated[tableIndex], columns: cols };
			setPreview({ ...preview, tables: updated });
		},
		[preview]
	);

	const handleColumnLabelChange = useCallback(
		(tableIndex: number, colIndex: number, label: string) => {
			if (!preview) {
				return;
			}
			const updated = [...preview.tables];
			const cols = [...updated[tableIndex].columns];
			cols[colIndex] = { ...cols[colIndex], label };
			updated[tableIndex] = { ...updated[tableIndex], columns: cols };
			setPreview({ ...preview, tables: updated });
		},
		[preview]
	);

	const handleCellEdit = useCallback(
		(tableIndex: number, rowIndex: number, colIndex: number, value: string) => {
			if (!preview) {
				return;
			}
			const updated = [...preview.tables];
			const rows = updated[tableIndex].rows.map((row, rIdx) => {
				if (rIdx === rowIndex) {
					const newRow = [...row];
					newRow[colIndex] = value;
					return newRow;
				}
				return row;
			});
			updated[tableIndex] = { ...updated[tableIndex], rows };
			setPreview({ ...preview, tables: updated });
		},
		[preview]
	);

	const handleRemoveRow = useCallback(
		(tableIndex: number, rowIndex: number) => {
			if (!preview) {
				return;
			}
			const updated = [...preview.tables];
			const rows = updated[tableIndex].rows.filter((_, idx) => idx !== rowIndex);
			updated[tableIndex] = { ...updated[tableIndex], rows };
			setPreview({ ...preview, tables: updated });
		},
		[preview]
	);

	const handleAddRow = useCallback(
		(tableIndex: number) => {
			if (!preview) {
				return;
			}
			const updated = [...preview.tables];
			const colCount = updated[tableIndex].columns.length;
			const newRow = Array(colCount).fill("");
			updated[tableIndex] = {
				...updated[tableIndex],
				rows: [...updated[tableIndex].rows, newRow],
			};
			setPreview({ ...preview, tables: updated });
		},
		[preview]
	);

	const handleRemoveTable = useCallback(
		(tableIndex: number) => {
			if (!preview || preview.tables.length <= 1) {
				return;
			}
			const updated = preview.tables.filter((_, idx) => idx !== tableIndex);
			setPreview({ ...preview, tables: updated });
			if (activeTableIndex >= updated.length) {
				setActiveTableIndex(updated.length - 1);
			}
		},
		[preview, activeTableIndex]
	);

	const handleSave = useCallback(async () => {
		if (!preview) {
			return;
		}
		setIsSaving(true);
		try {
			await saveAiImportTables(rfxId, {
				tables: preview.tables.map((t) => ({
					name: t.name,
					columns: t.columns.map((c) => ({
						name: c.label,
						type: c.detectedType,
					})),
					rows: t.rows,
				})),
			});
			onImportComplete();
			onClose();
		} catch {
			setError("Failed to save imported tables. Please try again.");
		} finally {
			setIsSaving(false);
		}
	}, [preview, rfxId, onImportComplete, onClose]);

	const handleReset = useCallback(() => {
		setPreview(null);
		setError("");
		setActiveTableIndex(0);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	}, []);

	const activeTable: AiImportTable | undefined = preview?.tables[activeTableIndex];

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="xl">
			<ModalOverlay />
			<ModalContent data-testid="ai-import-modal">
				<ModalHeader>Import from Spreadsheet</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{!preview && !isAnalysing && (
						<div
							className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-8"
							data-testid="file-upload-area"
						>
							<FiUpload className="mb-3 text-gray-400" size={32} />
							<Text className="mb-2 text-sm font-medium text-gray-700">
								Select an Excel file to import
							</Text>
							<Text className="mb-4 text-xs text-gray-500">
								Supports .xlsx and .xls files. Tables will be automatically detected.
							</Text>
							<input
								ref={fileInputRef}
								type="file"
								accept=".xlsx,.xls"
								onChange={handleFileSelect}
								className="hidden"
								data-testid="ai-file-input"
							/>
							<Button
								size="sm"
								btntype="primary"
								onClick={() => fileInputRef.current?.click()}
								data-testid="ai-select-file-btn"
							>
								Choose File
							</Button>
						</div>
					)}

					{isAnalysing && (
						<div
							className="flex flex-col items-center justify-center py-8"
							data-testid="analysing-spinner"
						>
							<Spinner size="lg" className="mb-3" />
							<Text className="text-sm text-gray-600">
								Analysing spreadsheet structure...
							</Text>
						</div>
					)}

					{error && (
						<Alert
							status="error"
							className="mt-3 rounded-md"
							data-testid="ai-import-error"
						>
							<AlertIcon />
							<Text className="text-sm">{error}</Text>
						</Alert>
					)}

					{preview && preview.rowLimitReached && (
						<Alert
							status="warning"
							className="mb-3 rounded-md"
							data-testid="row-limit-warning"
						>
							<AlertIcon />
							<Text className="text-sm">
								This spreadsheet contains more rows than the maximum limit
								({preview.maxRows}). Only the first {preview.maxRows} rows
								per table have been imported.
							</Text>
						</Alert>
					)}

					{preview && activeTable && (
						<div data-testid="ai-import-preview">
							<div className="mb-3 flex items-center justify-between">
								<Text className="text-sm font-medium text-gray-700">
									{preview.tables.length} table{preview.tables.length !== 1 ? "s" : ""} detected
								</Text>
								<Button
									size="xs"
									btntype="secondary"
									onClick={handleReset}
									data-testid="ai-reset-btn"
								>
									Choose Different File
								</Button>
							</div>

							{preview.tables.length > 1 && (
								<div
									className="mb-3 flex gap-1 overflow-x-auto"
									data-testid="table-tabs"
								>
									{preview.tables.map((table, idx) => (
										<button
											key={idx}
											type="button"
											className={cn(
												"rounded-t-md border border-b-0 px-3 py-1.5 text-xs font-medium",
												idx === activeTableIndex
													? "border-[#EAECF0] bg-white text-gray-900"
													: "border-transparent bg-gray-100 text-gray-500 hover:text-gray-700"
											)}
											onClick={() => setActiveTableIndex(idx)}
											data-testid={`table-tab-${idx}`}
										>
											{table.name}
										</button>
									))}
								</div>
							)}

							<div className="mb-2 flex items-center gap-2">
								<input
									type="text"
									className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm font-medium"
									value={activeTable.name}
									onChange={(e) =>
										handleTableNameChange(activeTableIndex, e.target.value)
									}
									aria-label="Table name"
									data-testid="table-name-input"
								/>
								{preview.tables.length > 1 && (
									<button
										type="button"
										className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
										onClick={() => handleRemoveTable(activeTableIndex)}
										title="Remove table"
										aria-label={`Remove table ${activeTable.name}`}
										data-testid="remove-table-btn"
									>
										<FiTrash2 size={14} />
									</button>
								)}
							</div>

							<div className="overflow-x-auto rounded-lg border border-[#EAECF0]">
								<table
									className="w-full table-auto"
									data-testid="ai-preview-table"
								>
									<caption className="sr-only">AI spreadsheet import preview</caption>
									<thead>
										<tr className="bg-blue-50">
											<th className={cn(cellBorder, "w-10 px-2 py-1 text-center text-xs font-medium text-gray-500")}>#</th>
											{activeTable.columns.map((col, idx) => (
												<th
													key={idx}
													className={cn(cellBorder, "px-2 py-1")}
													style={{ minWidth: "140px" }}
												>
													<select
														className="w-full rounded border border-gray-300 bg-white px-1 py-0.5 text-xs"
														value={col.detectedType}
														onChange={(e) =>
															handleColumnTypeChange(
																activeTableIndex,
																idx,
																e.target.value
															)
														}
														aria-label={`Column type for column ${idx + 1}`}
													data-testid={`ai-col-type-${idx}`}
													>
														{COLUMN_TYPE_OPTIONS.map((opt) => (
															<option key={opt.value} value={opt.value}>
																{opt.label}
															</option>
														))}
													</select>
												</th>
											))}
											<th className="w-10 bg-blue-50" />
										</tr>
										<tr className="bg-[#F9FAFB]">
											<th className={cn(cellBorder, "w-10 px-2 py-1")} />
											{activeTable.columns.map((col, idx) => (
												<th
													key={idx}
													className={cn(cellBorder, "px-2 py-1")}
												>
													<input
														type="text"
														className="w-full rounded border border-gray-300 bg-white px-1 py-0.5 text-xs font-medium"
														value={col.label}
														onChange={(e) =>
															handleColumnLabelChange(
																activeTableIndex,
																idx,
																e.target.value
															)
														}
														aria-label={`Column label for column ${idx + 1}`}
													data-testid={`ai-col-label-${idx}`}
													/>
												</th>
											))}
											<th className="w-10 bg-[#F9FAFB]" />
										</tr>
									</thead>
									<tbody>
										{activeTable.rows.map((row, rIdx) => (
											<tr key={rIdx}>
												<td className={cn(cellBorder, "w-10 text-center text-xs text-gray-500 bg-gray-50")}>
													{rIdx + 1}
												</td>
												{row.map((cell, cIdx) => (
													<td
														key={cIdx}
														className={cn(cellBorder, "px-1 py-1")}
													>
														<input
															type="text"
															className="w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-xs hover:border-gray-300 focus:border-blue-400 focus:outline-none"
															value={cell}
															onChange={(e) =>
																handleCellEdit(
																	activeTableIndex,
																	rIdx,
																	cIdx,
																	e.target.value
																)
															}
															data-testid={`ai-cell-${rIdx}-${cIdx}`}
														/>
													</td>
												))}
												<td className="px-1">
													<button
														type="button"
														className="rounded p-0.5 text-red-400 hover:text-red-600"
														onClick={() =>
															handleRemoveRow(activeTableIndex, rIdx)
														}
														title="Remove row"
														aria-label={`Remove row ${rIdx + 1}`}
														data-testid={`ai-remove-row-${rIdx}`}
													>
														<FiTrash2 size={10} />
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="mt-2">
								<Button
									size="xs"
									btntype="secondary"
									onClick={() => handleAddRow(activeTableIndex)}
									data-testid="ai-add-row-btn"
								>
									<FiPlus className="mr-1" />
									Add Row
								</Button>
							</div>
						</div>
					)}
				</ModalBody>
				<ModalFooter>
					<Button
						btntype="secondary"
						onClick={onClose}
						className="mr-3"
						data-testid="ai-cancel-btn"
					>
						Cancel
					</Button>
					{preview && (
						<Button
							btntype="primary"
							onClick={handleSave}
							disabled={isSaving}
							data-testid="ai-save-btn"
						>
							{isSaving ? "Importing..." : "Import Tables"}
						</Button>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export { AiSpreadsheetImport };
